import { useState, useEffect } from 'react';
import {
  FileText, X, Save, Link, Check,
  BarChart2, Type, Tag, AlertTriangle,
  ToggleLeft, ToggleRight, Calendar, Lock, Unlock, Smartphone
} from 'lucide-react';
import { getPublicLink, filesAPI } from '../api';
import AccessKeysPanel from './AccessKeysPanel';
import ExternalSubsPanel from './ExternalSubsPanel';
import './FileEditor.css';

function FileEditor({ file, onSave, onClose, groups, onGroupChange, onDirtyChange, onKeysChanged, onLockedChange, onContentReplaced }) {
  const [content, setContent] = useState(file.content);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [showCopyNotification, setShowCopyNotification] = useState(false);

  // File settings (mode + endDate)
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [fileSettings, setFileSettings] = useState({ mode: 'active', endDate: null, locked: false });
  const [pendingEndDate, setPendingEndDate] = useState('');

  const [opError, setOpError] = useState('');

  const isDirty = content !== file.content;

  useEffect(() => { onDirtyChange?.(isDirty); }, [isDirty]);

  // Auto-clear opError after 5s
  useEffect(() => {
    if (!opError) return;
    const t = setTimeout(() => setOpError(''), 5000);
    return () => clearTimeout(t);
  }, [opError]);

  // Ctrl+S to save
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (isDirty && !isSaving && !fileSettings.locked) handleSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isDirty, isSaving, content, file.name, fileSettings.locked]);

  useEffect(() => {
    setContent(file.content);
    setSaveStatus('');
  }, [file.name, file.content]);

  useEffect(() => {
    if (isInitialLoad || file.name) {
      loadFileSettings();
      setIsInitialLoad(false);
    }
  }, [file.name]);

  const loadFileSettings = async () => {
    try {
      const res = await filesAPI.getSettings(file.name);
      setFileSettings({
        mode: res.data.mode || 'active',
        endDate: res.data.endDate || null,
        locked: res.data.locked === true,
        happOnly: res.data.happOnly === true,
      });
      setPendingEndDate(res.data.endDate ? res.data.endDate.slice(0, 10) : '');
    } catch (err) {
      if (err.response?.status !== 401) console.error('Failed to load file settings:', err);
    }
  };

  const handleSetMode = async (mode) => {
    try {
      await filesAPI.setMode(file.name, mode);
      setFileSettings(prev => ({ ...prev, mode }));
    } catch (err) {
      setOpError('Ошибка смены статуса: ' + (err.response?.data?.error || err.message));
    }
  };

  // Замок: файл продолжает раздаваться клиентам, но массовое/групповое редактирование и
  // авто-провижн его пропускают, а ручное сохранение отключено (сервер вернёт 423).
  const handleToggleLocked = async () => {
    const next = !fileSettings.locked;
    try {
      await filesAPI.setLocked(file.name, next);
      setFileSettings(prev => ({ ...prev, locked: next }));
      onLockedChange?.(file.name, next);
    } catch (err) {
      setOpError('Ошибка замка: ' + (err.response?.data?.error || err.message));
    }
  };

  // Строгий режим: ссылка открывается только в Happ. Единственный способ добиться
  // честного «одно устройство на ключ» — у Hiddify/V2Box нет никакого install-ID,
  // и второй такой же клиент на той же платформе от владельца неотличим.
  const handleToggleHappOnly = async () => {
    const next = !fileSettings.happOnly;
    try {
      await filesAPI.setHappOnly(file.name, next);
      setFileSettings(prev => ({ ...prev, happOnly: next }));
    } catch (err) {
      setOpError('Ошибка строгого режима: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSetEndDate = async (date) => {
    try {
      await filesAPI.setEndDate(file.name, date || null);
      setFileSettings(prev => ({ ...prev, endDate: date || null }));
    } catch (err) {
      setOpError('Ошибка установки даты: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleClose = () => {
    if (isDirty && !confirm('Есть несохранённые изменения. Закрыть?')) return;
    onClose();
  };

  const handleSave = async () => {
    // Кнопка активна всегда, а не только при изменениях, поэтому нажатие с пустым полем
    // молча стирало файл целиком — так уже потеряли содержимое yhlas после обновления
    // внешних подписок. Опустошение файла — не рядовое сохранение, спрашиваем.
    if (!content.trim() && file.content?.trim()) {
      const ok = confirm(
        `Файл "${file.name}" сейчас не пустой, а поле — пустое.\n` +
        'Сохранение сотрёт все ссылки. Точно стереть?'
      );
      if (!ok) return;
    }

    setIsSaving(true);
    setSaveStatus('');
    try {
      await onSave(file.name, content);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      setSaveStatus('error');
      setOpError(error.message || 'Ошибка сохранения');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyLink = async () => {
    const link = getPublicLink(file.name);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
      } else {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.style.cssText = 'position:fixed;left:-9999px';
        document.body.appendChild(ta);
        ta.focus(); ta.select();
        document.execCommand('copy');
        ta.remove();
      }
      setShowCopyNotification(true);
      setTimeout(() => setShowCopyNotification(false), 2000);
    } catch (error) {
      alert('Не удалось скопировать: ' + error.message);
    }
  };

  const linesCount = content.split('\n').filter(l => l.trim()).length;
  const publicLink = getPublicLink(file.name);
  const displayName = file.name.replace(/\.json$/, '');

  return (
    <div className="file-editor">
      <div className="editor-header">
        <div className="editor-title">
          <span className="editor-icon"><FileText size={16} /></span>
          <h3>{displayName}</h3>
          {isDirty && <span className="dirty-indicator" title="Несохранённые изменения" />}
        </div>
        <div className="editor-header-actions">
          {opError && (
            <span className="save-success" style={{ color: 'var(--danger)', background: 'var(--danger-bg)' }}>
              <AlertTriangle size={13} /> {opError}
            </span>
          )}
          {saveStatus === 'success' && !opError && (
            <span className="save-success"><Check size={13} /> Сохранено</span>
          )}
          <button
            onClick={handleSave}
            disabled={isSaving || fileSettings.locked}
            className="btn btn-success btn-sm"
            title={fileSettings.locked ? 'Файл под замком — снимите замок внизу, чтобы редактировать' : ''}
          >
            {fileSettings.locked ? <Lock size={13} /> : <Save size={13} />}
            {isSaving ? 'Сохранение...' : (fileSettings.locked ? 'Под замком' : 'Сохранить')}
          </button>
          <button onClick={handleClose} className="btn btn-ghost btn-sm btn-icon">
            <X size={15} />
          </button>
        </div>
      </div>

      {groups && onGroupChange && (
        <div className="editor-group-row">
          <span className="editor-group-label"><Tag size={12} /> Группа:</span>
          <select
            className="editor-group-select"
            value={file.group || ''}
            onChange={(e) => onGroupChange(file.name, e.target.value || null)}
          >
            <option value="">— Без группы —</option>
            {Object.entries(groups).map(([id, g]) => (
              <option key={id} value={id}>{g.name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="editor-toolbar">
        <div className="editor-stats">
          <span className="stat-chip">
            <BarChart2 size={11} /> {linesCount} строк
          </span>
          <span className="stat-chip">
            <Type size={11} /> {content.length} симв.
          </span>
        </div>
      </div>

      <div className="editor-content">
        <textarea
          className="textarea"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          readOnly={fileSettings.locked}
          placeholder="Вставьте VPN-ссылки, каждая на новой строке..."
          spellCheck={false}
        />
      </div>

      <div className="editor-footer">
        {/* ── Статус ─────────────────────────── */}
        <div className="footer-section">
          <div className="section-label">
            <span className="section-label-text">
              {fileSettings.mode === 'active'
                ? <ToggleRight size={13} color="var(--success)" />
                : <ToggleLeft size={13} color="var(--danger)" />
              } Статус файла
            </span>
          </div>
          <div className="status-toggle">
            <button
              className={`status-btn ${fileSettings.mode === 'active' ? 'active-mode' : ''}`}
              onClick={() => handleSetMode('active')}
            >
              <Check size={11} /> Активен
            </button>
            <button
              className={`status-btn ${fileSettings.mode === 'nonactive' ? 'inactive-mode' : ''}`}
              onClick={() => handleSetMode('nonactive')}
            >
              <X size={11} /> Неактивен
            </button>

            {/* Замок содержимого — отдельный от статуса переключатель: клиенты файл получают
                как обычно, но массовое редактирование его пропускает и запись запрещена */}
            <button
              className={`lock-switch ${fileSettings.locked ? 'locked' : ''}`}
              onClick={handleToggleLocked}
              title={fileSettings.locked
                ? 'Замок включён: файл пропускается при массовом редактировании и авто-провижне. Нажми, чтобы разрешить запись'
                : 'Поставить замок: запретить запись в файл (массовое редактирование его пропустит)'}
            >
              {fileSettings.locked ? <Lock size={11} /> : <Unlock size={11} />}
              <span className="lock-switch-track"><span className="lock-switch-knob" /></span>
              {fileSettings.locked ? 'Замок' : 'Без замка'}
            </button>

            {/* Строгий режим привязки — про доступ клиента, а не про запись в файл */}
            <button
              className={`lock-switch ${fileSettings.happOnly ? 'locked' : ''}`}
              onClick={handleToggleHappOnly}
              title={fileSettings.happOnly
                ? 'Строгий режим включён: подписку отдаём только Happ, привязка к устройству железная. Нажми, чтобы разрешить любые приложения'
                : 'Включить строгий режим: только Happ. У Hiddify/V2Box нет ID устройства, поэтому второй такой же клиент от владельца неотличим'}
            >
              <Smartphone size={11} />
              <span className="lock-switch-track"><span className="lock-switch-knob" /></span>
              {fileSettings.happOnly ? 'Только Happ' : 'Любое приложение'}
            </button>
          </div>
          {fileSettings.locked && (
            <div className="lock-hint">
              🔒 Содержимое заморожено: массовое и групповое редактирование пропустят этот файл,
              авто-провижн не добавит и не снимет ссылки. Клиентам файл раздаётся как обычно.
            </div>
          )}
          {fileSettings.happOnly && (
            <div className="lock-hint">
              🔐 Только Happ: ссылка не откроется в Hiddify и V2Box (403 с подсказкой поставить Happ).
              Взамен ключ намертво привязан к одной установке — второе устройство подписку не получит.
            </div>
          )}
          {!fileSettings.happOnly && (
            <div className="lock-hint">
              ⚠️ Любое приложение: у Hiddify и V2Box нет ID устройства, различаем их только по
              «приложение + платформа». Второй Hiddify на такой же платформе пройдёт как владелец —
              если поймали шеринг, включай «Только Happ».
            </div>
          )}
        </div>

        {/* ── Дата окончания ─────────────────── */}
        <div className="footer-section">
          <div className="section-label">
            <span className="section-label-text"><Calendar size={12} /> Дата окончания</span>
          </div>
          <div className="enddate-row">
            <input
              type="date"
              className="enddate-input"
              value={pendingEndDate}
              onChange={(e) => setPendingEndDate(e.target.value)}
              onBlur={(e) => {
                const saved = fileSettings.endDate ? fileSettings.endDate.slice(0, 10) : '';
                if (e.target.value !== saved) handleSetEndDate(e.target.value);
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleSetEndDate(pendingEndDate)}
            />
            {pendingEndDate && (
              <button
                className="btn btn-ghost btn-xs btn-icon"
                title="Убрать дату"
                onClick={() => { setPendingEndDate(''); handleSetEndDate(null); }}
              >
                <X size={12} />
              </button>
            )}
          </div>
          {fileSettings.endDate && new Date() > new Date(fileSettings.endDate) && (
            <span className="enddate-expired"><AlertTriangle size={10} /> Истёк</span>
          )}
          <p className="enddate-hint">После этой даты клиент получит ошибку 403</p>
        </div>

        {/* ── Публичная ссылка ───────────────── */}
        <div className="footer-section">
          <div className="section-label">
            <span className="section-label-text"><Link size={12} /> Базовая ссылка</span>
          </div>
          <div className="link-group">
            <input type="text" className="input input-sm link-input" value={publicLink} readOnly />
            <button onClick={handleCopyLink} className="btn btn-primary btn-sm btn-copy">
              {showCopyNotification ? <><Check size={13} /> Скопировано</> : 'Копировать'}
            </button>
          </div>
          <p className="enddate-hint">Клиентам нужна ссылка с ключом — смотри раздел ниже</p>
          {showCopyNotification && (
            <span className="copy-toast"><Check size={11} /> Скопировано!</span>
          )}
        </div>

        {/* ── Привязанные чужие подписки ─────── */}
        {/* Обновление источников переписывает файл на сервере, поэтому новое содержимое
            отдаём наверх — иначе в textarea остался бы старый текст и следующее
            «Сохранить» затёрло бы только что вписанные ссылки. Пока в редакторе есть
            несохранённые правки, кнопка обновления заблокирована. `content` панели нужен,
            чтобы сказать в подтверждении, сколько строк будет стёрто заменой. */}
        <ExternalSubsPanel
          fileName={file.name}
          onError={setOpError}
          isDirty={isDirty}
          content={content}
          onContentReplaced={(text) => onContentReplaced?.(file.name, text)}
        />

        {/* ── Ключи доступа ──────────────────── */}
        <AccessKeysPanel
          fileName={file.name}
          isAdmin
          onKeysChanged={onKeysChanged}
          onError={setOpError}
        />
      </div>
    </div>
  );
}

export default FileEditor;
